import { useEffect, useState } from 'react'

// Debug log to confirm file loading
export const AI_COMMAND_UTILS_LOADED = true;

// Base interface for all AI commands
export interface AICommand {
  'sysagent-cmd': string
  'sysagent-response': string
  processing: boolean // Standard flag for processing status
  processedAt?: number // Timestamp when processing completed
  timestamp?: number // Timestamp when command was created
  message?: string // Status message
  error?: string // Error message if failed
  [key: string]: any
}

// Common command types across the application
export enum AICommandType {
  DOCUMENT_PROCESS = 'DocumentNode-Process',
  SECTION_PROCESS = 'SectionCommand-Process',
  QUICK_START = 'QuickStartNode-Process',
  DIAGRAMS_GENERATE = 'NodeDiagrams-AI-Generate',
  DIAGRAMS_DESIGN = 'NodeDiagrams-AI-Design',
  DIAGRAMS_MERMAID_GENERATE = 'NodeDiagramsMermaid-AI-Generate',
  DIAGRAMS_MERMAID_DESIGN = 'NodeDiagramsMermaid-AI-Design',
  INTELLICA_PROCESS = 'NodeIntellica-Process',
  FILEMINER_SUGGESTIONS = 'NodeFileMiner-Suggestions',
  DOCUMENT_DESIGNER = 'NodeDocumentDesigner-Generate',
  RESEARCHER_PROCESS = 'NodeResearcher-Process',
  FILEMINER_PROCESS = 'NodeFileMiner-Process',
  IMAGEGENERATOR_PROCESS = 'NodeImageGenerator-Process',
  TITLE_UPDATE = 'TitleUpdate-Process',
  WEB_EXTRACT = 'WebExtract-Process',
  NODE_RESOURCE = 'NodeResource-Process',
  NODE_RESOURCE_PDF = 'NodeResourcePDF-Process',
  NODE_GOOGLE_DRIVE = 'NodeGoogleDrive-Process',
  DOMAINKNOWLEDGE_PROCESS = 'NodeDomainKnowledge-Process',
  JAVASCRIPT_COMMAND = 'JavascriptCommand-Process',
  AGENT_COMMAND = 'AgentCommand-Process',
  QUICKSTART_COMMAND = 'QuickStartCommand-Process',
  SMART_NOTES_ANALYSIS = 'SmartNotesNode-Analysis',
  DIKW_EXTRACTION = 'NodeDIKW-Extraction',
  WEB_SEARCH_PROCESS = 'WebSearch-Process'
}

// Command status states
export enum AICommandStatus {
  STARTED = 'Started',
  FINISHED = 'Finished',
  ERROR = 'Error',
  NONE = '',
}

/**
 * Creates a standardized AI command object
 */
// In aiCommandUtils.ts
export function createAICommand(
  type: AICommandType,
  statusOrPayload?: AICommandStatus | Record<string, any>,
  payload: Record<string, any> = {}
): AICommand {
  // Handle the case where second parameter is payload (most common usage)
  let status: AICommandStatus = AICommandStatus.STARTED
  let actualPayload: Record<string, any> = {}

  if (typeof statusOrPayload === 'object' && statusOrPayload !== null) {
    // Second parameter is payload
    actualPayload = statusOrPayload
  } else if (typeof statusOrPayload === 'string') {
    // Second parameter is status
    status = statusOrPayload
    actualPayload = payload
  }

  return {
    'sysagent-cmd':
      status === AICommandStatus.STARTED ? `${type}-${AICommandStatus.STARTED}` : '',
    'sysagent-response':
      status !== AICommandStatus.STARTED ? `${type}-${status}` : '',
    processing: status === AICommandStatus.STARTED,
    timestamp: Date.now(),
    processedAt: status !== AICommandStatus.STARTED ? Date.now() : undefined,
    ...actualPayload,
  }
}

/**
 * Checks if a command is complete (successful)
 */
export function isCommandComplete(
  command: AICommand | null | undefined,
  type: AICommandType
): boolean {
  return !!(
    command &&
    command['sysagent-cmd'] === '' &&
    command['sysagent-response'] === `${type}-${AICommandStatus.FINISHED}`
  )
}

/**
 * Checks if a command has errored
 */
export function isCommandError(
  command: AICommand | null | undefined,
  type: AICommandType
): boolean {
  return !!(
    command &&
    command['sysagent-cmd'] === '' &&
    command['sysagent-response'] === `${type}-${AICommandStatus.ERROR}`
  );
}

/**
 * Checks if a command is in progress
 */
export function isCommandInProgress(
  command: AICommand | null | undefined,
  type: AICommandType
): boolean {
  return !!(
    command &&
    command['sysagent-cmd'] === `${type}-${AICommandStatus.STARTED}` &&
    command['sysagent-response'] === ''
  )
}

/**
 * Creates a node-specific command payload with the given content
 */
export function createNodeCommandPayload(
  nodeId: string,
  content: Record<string, any>
): Record<string, any> {
  return {
    [nodeId]: content,
    // Add common fields that are used across multiple command types
    processing: true,
    timestamp: Date.now(),
  }
}

/**
 * Returns the appropriate node status based on the command state
 */
export function getNodeStatusFromCommand(
  command: AICommand | null | undefined,
  type: AICommandType
): 'success' | 'loading' | 'error' | '' {
  if (!command) return ''

  if (isCommandInProgress(command, type) || command.processing) {
    return 'loading'
  } else if (isCommandComplete(command, type)) {
    return 'success'
  } else if (isCommandError(command, type)) {
    return 'error'
  }

  return ''
}

/**
 * Hook for managing AI command state and processing
 */
export function useAICommandState(
  initialCommand: AICommand | null | undefined,
  commandType: AICommandType
) {
  const [isProcessing, setIsProcessing] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [nodeStatus, setNodeStatus] = useState<
    'success' | 'loading' | 'error' | ''
  >('')

  useEffect(() => {
    //console.log('useAICommandState input:', { initialCommand, commandType })
    if (!initialCommand) {
      setIsProcessing(false)
      setNodeStatus('')
      setStatusMessage('')
      return
    }

    // Prioritize the explicit processing flag
    if (initialCommand.processing === true) {
      setIsProcessing(true)
      setNodeStatus('loading')
      setStatusMessage(initialCommand.message || `Processing ${commandType}...`)
      return
    }

    // Then check command states
    if (isCommandInProgress(initialCommand, commandType)) {
      setIsProcessing(true)
      setNodeStatus('loading')
      setStatusMessage(initialCommand.message || `Processing ${commandType}...`)
    } else if (isCommandComplete(initialCommand, commandType)) {
      setIsProcessing(false)
      setNodeStatus('success')
      setStatusMessage(
        initialCommand.message || `${commandType} completed successfully`
      )
    } else if (isCommandError(initialCommand, commandType)) {
      setIsProcessing(false)
      setNodeStatus('error')
      setStatusMessage(
        initialCommand.error || `Error processing ${commandType}`
      )
    } else {
      // Default case when processing is false and no specific command state
      setIsProcessing(false)
      setNodeStatus('')
      setStatusMessage(initialCommand.message || '')
    }
    //console.log('useAICommandState output:', { isProcessing, statusMessage, nodeStatus })
  }, [initialCommand, commandType]);

  return {
    isProcessing,
    statusMessage,
    setStatusMessage,
    nodeStatus,
  }
}
